import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { getVetsSlotsReportData } from "../../../../redux/reducers/reportSlice";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { minutesToHours } from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./VetSlots.css";

const tableHdrs = [
  "vetName",
  "slotsAvailable",
  "slotsBooked",
  "slotsRemaining",
  "overtimetime",
  "utilizationPercentage",
];

const VetsSlotsReport = () => {
  const dispatch = useDispatch();
  const [selectedFilter, setSelectedFilter] = useState("week");
  const [dateRange, setDateRange] = useState("2026-08-04 to 2026-08-10");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs);
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const { vetSlots } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    onApiCall(1, selectedFilter);
  }, [dispatch, selectedFilter]);

  const onApiCall = (pge = 1, filter = null) => {
    setPage(pge);
    const reqFilter = filter ? filter : selectedFilter;
    const reqObj = {
      period: reqFilter,
      topN: 2,
      page: pge,
      itemSize: rowsPerPage,
    };
    dispatch(getVetsSlotsReportData(reqObj));
  };

  const getSlotsReportDataList = (vetSlotsData) => {
    const payload = vetSlotsData?.data || vetSlotsData;
    if (Array.isArray(payload?.bookedVsAvailable)) return payload.bookedVsAvailable;
    if (Array.isArray(payload?.roster)) return payload.roster;
    if (Array.isArray(payload?.vets)) return payload.vets;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.byVet)) return payload.byVet;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const reportData = vetSlots?.data || vetSlots || {};
  const summaryInfo = reportData?.summary || {};

  // Date Range Display Sync
  useEffect(() => {
    if (reportData?.dateRangeDisplay) {
      setDateRange(reportData.dateRangeDisplay);
    }
  }, [reportData?.dateRangeDisplay]);

  const rawRosterList = getSlotsReportDataList(vetSlots);

  const tableDataList = rawRosterList.map((li) => ({
    ...li,
    vetName: li?.doctorName || li?.vetName || li?.name || "",
    slotsAvailable: li?.available ?? li?.slotsAvailable ?? 0,
    slotsBooked: li?.booked ?? li?.slotsBooked ?? 0,
    slotsRemaining:
      li?.slotsRemaining ??
      Math.max(0, (li?.available ?? 0) - (li?.booked ?? 0)),
    overtimetime:
      (li?.overtimetime ?? "")?.toString()?.length > 0
        ? minutesToHours(li?.overtimetime)
        : "",
    utilizationPercentage:
      li?.utilizationDisplay ??
      (li?.utilization !== undefined
        ? `${li.utilization}%`
        : li?.utilizationPercentage ?? "0%"),
  }));

  const totalRecords =
    reportData?.totalRecords ??
    reportData?.totalCount ??
    vetSlots?.totalRecords ??
    tableDataList.length;
  const totalPage = Math.ceil(totalRecords / rowsPerPage);

  const summaryCards = [
    {
      title: "Avg utilization",
      value:
        summaryInfo?.avgUtilizationDisplay ??
        (summaryInfo?.avgUtilization !== undefined
          ? `${summaryInfo.avgUtilization}%`
          : reportData?.avgUtilization ?? "0%"),
    },
    {
      title: "Total slots booked",
      value:
        summaryInfo?.totalSlotsBooked ??
        summaryInfo?.slotsBooked ??
        reportData?.totalSlotsBooked ??
        "0",
    },
    {
      title: "Total slots available",
      value:
        summaryInfo?.totalSlotsAvailable ??
        summaryInfo?.slotsAvailable ??
        reportData?.totalSlotsAvailable ??
        "0",
    },
  ];

  // Booked vs Available Bar Chart Data
  const bookedVsAvailableList = Array.isArray(reportData?.bookedVsAvailable)
    ? reportData.bookedVsAvailable
    : Array.isArray(reportData?.bars)
    ? reportData.bars
    : [];

  let barCategories = ["Vivek", "Supervet", "Luke Dunphy"];
  let bookedSeriesData = [15, 5, 4];
  let availableSeriesData = [0, 0, 0];

  if (bookedVsAvailableList.length > 0) {
    barCategories = bookedVsAvailableList.map(
      (item) => item?.doctorName || item?.vetName || item?.name || "",
    );
    bookedSeriesData = bookedVsAvailableList.map((item) => item?.booked ?? 0);
    availableSeriesData = bookedVsAvailableList.map(
      (item) => item?.available ?? 0,
    );
  } else if (reportData?.bookedVsAvailable?.categories) {
    barCategories = reportData.bookedVsAvailable.categories;
    bookedSeriesData = reportData.bookedVsAvailable.booked || [];
    availableSeriesData = reportData.bookedVsAvailable.available || [];
  }

  const bookedVsAvailableOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 3,
      },
    },
    colors: ["#1976D2", "#90CAF9"],
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: barCategories,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontFamily: "Montserrat, sans-serif",
      fontSize: "12px",
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
  };

  const bookedVsAvailableSeries = [
    {
      name: "Booked",
      data: bookedSeriesData,
    },
    {
      name: "Available",
      data: availableSeriesData,
    },
  ];

  // Utilization Trend Line Chart Data
  const trendObj = reportData?.utilizationTrend || {};
  const trendRawSeries = Array.isArray(trendObj?.series)
    ? trendObj.series
    : Array.isArray(reportData?.trend)
    ? reportData.trend
    : [];

  const trendCategories =
    trendObj?.weeks ||
    trendObj?.days ||
    trendObj?.months ||
    trendObj?.categories ||
    trendObj?.periods || [
      "W36",
      "W37",
    ];

  let utilizationTrendSeries = [
    {
      name: "Vivek",
      data: [0.0, 0.0],
    },
    {
      name: "Supervet",
      data: [0.0, 0.0],
    },
  ];

  if (trendRawSeries.length > 0) {
    utilizationTrendSeries = trendRawSeries.map((item) => ({
      name: item?.doctorName || item?.name || item?.vetName || "Vet",
      data: Array.isArray(item?.data)
        ? item.data
        : Array.isArray(item?.values)
        ? item.values
        : [],
    }));
  }

  const utilizationTrendOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 2.5,
    },
    colors: ["#1976D2", "#E65100", "#10B981", "#9C27B0", "#FFC225"],
    markers: {
      size: 0,
    },
    xaxis: {
      categories: trendCategories,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        formatter: (val) => `${val}%`,
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Montserrat, sans-serif",
      fontSize: "12px",
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  return (
    <div className="vet-slots-container">
      {/* Title */}
      <h2 className="vet-slots-title">Utilization vs capacity</h2>

      {/* Top Filter Bar */}
      <div className="vet-slots-filter-bar">
        <div className="vet-slots-date-picker">
          <Select
            size="small"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            sx={{
              height: 36,
              fontSize: 13,
              fontFamily: "Montserrat, sans-serif",
              borderRadius: "6px",
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
            }}
          >
            <MenuItem value={dateRange}>{dateRange}</MenuItem>
            <MenuItem value="2026-08-04 to 2026-08-10">
              2026-08-04 to 2026-08-10
            </MenuItem>
            <MenuItem value="2026-07-28 to 2026-08-03">
              2026-07-28 to 2026-08-03
            </MenuItem>
            <MenuItem value="2026-07-01 to 2026-07-31">
              2026-07-01 to 2026-07-31
            </MenuItem>
          </Select>
        </div>

        <div className="vet-slots-period-group">
          {[
            { label: "Today", value: "today" },
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
            { label: "Quarter", value: "quarter" },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              className={`vet-slots-period-btn ${selectedFilter === item.value ? "active" : ""
                }`}
              onClick={() => setSelectedFilter(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="vet-slots-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="vet-slots-card" key={idx}>
            <div className="vet-slots-card-title">{card.title}</div>
            <div className="vet-slots-card-value">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="vet-slots-charts-grid">
        <div className="vet-slots-chart-card">
          <div className="vet-slots-chart-header">
            Booked vs available (click a bar to filter table)
          </div>
          <Chart
            options={bookedVsAvailableOptions}
            series={bookedVsAvailableSeries}
            type="bar"
            height={300}
          />
        </div>

        <div className="vet-slots-chart-card">
          <div className="vet-slots-chart-header">Utilization % trend</div>
          <Chart
            options={utilizationTrendOptions}
            series={utilizationTrendSeries}
            type="line"
            height={300}
          />
        </div>
      </div>

      {/* Drill-down Header */}
      <div className="vet-slots-drilldown-header">Drill-down — per vet</div>

      {/* Table Action Bar */}
      <div className="flex-row-ali-cen mt5 mh20">
        <Typography className="font-medium fs12 gray28 ml10">
          Number of Records: {totalRecords}
        </Typography>
        <div
          className="flex-row-ali-cen ml20 cursor"
          onClick={handleEditColumn}
        >
          <EditOutlinedIcon sx={w20h20BlueIcon} />
          <Typography className="blue-color fs16 font-medium ml5">
            Edit columns ({crntTblHeders?.length})
          </Typography>
        </div>
        <div className="flex-row-ali-cen ml20 cursor" onClick={handleOpenChart}>
          <AutoGraphIcon sx={w20h20BlueIcon} />
        </div>
      </div>

      <div className="mtMin10">
        <ReportTable
          datas={tableDataList?.map((li) => ({
            ...li,
            overtimetime:
              (li?.overtimetime ?? "")?.toString()?.length > 0
                ? minutesToHours(li?.overtimetime)
                : "",
          }))}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {totalPage > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default VetsSlotsReport;
